import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEllipsisV, FaTimes, FaPlus, FaFilePdf } from 'react-icons/fa';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import adminCrmService from '../../../services/adminCrmService';
import propertyService from '../../../services/propertyService';
import '../../../assets/styles/PersonalIDs.css';
import '../../assets/styles/CRMOpenLead.css';
import arrowleftt from '../../assets/img/arrowleftt.svg';
import downloadIcon from '../../assets/img/invoice.svg';
import { getPdfFirstPageThumbnail } from '../../../utils/pdfPreview';

const API_ORIGIN = process.env.REACT_APP_API_BASE_URL || 'https://sandbox.rebax.in';
const MAX_PDF_SIZE = 25 * 1024 * 1024; // matches propertyService.uploadDocument limit

const getFileUrl = (filePath) => {
  if (!filePath || typeof filePath !== 'string') return null;
  if (filePath.startsWith('http') || filePath.startsWith('blob:') || filePath.startsWith('data:')) return filePath;
  return `${API_ORIGIN}/${filePath.replace(/^\//, '')}`;
};

const getFileName = (filePath) => {
  if (!filePath) return '';
  const parts = String(filePath).split('?')[0].split('/');
  return parts[parts.length - 1] || 'Attachment.pdf';
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toInputDate = (value) => (value ? String(value).slice(0, 10) : '');

export default function AdminCrmMandateProjects() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, userRole } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rights, setRights] = useState([]);
  const [menuOpen, setMenuOpen] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingRight, setEditingRight] = useState(null);
  const [builderName, setBuilderName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingFilePath, setExistingFilePath] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const [modalThumb, setModalThumb] = useState(null);
  const [modalThumbLoading, setModalThumbLoading] = useState(false);
  const [listThumbs, setListThumbs] = useState({});

  useEffect(() => { if (userId) fetchRights(); }, [userId]);

  // First-page preview of the PDF currently selected/attached in the modal
  useEffect(() => {
    const source = selectedFile || getFileUrl(existingFilePath);
    if (!showModal || !source) {
      setModalThumb(null);
      setModalThumbLoading(false);
      return;
    }

    let cancelled = false;
    setModalThumb(null);
    setModalThumbLoading(true);
    getPdfFirstPageThumbnail(source, { maxWidth: 320 })
      .then(dataUrl => { if (!cancelled) setModalThumb(dataUrl); })
      .catch(err => console.error('Error generating PDF preview:', err))
      .finally(() => { if (!cancelled) setModalThumbLoading(false); });

    return () => { cancelled = true; };
  }, [showModal, selectedFile, existingFilePath]);

  // First-page thumbnails for the list, keyed by file_path so a replaced PDF re-renders
  useEffect(() => {
    rights.forEach(right => {
      if (!right.file_path || listThumbs[right.file_path]) return;
      getPdfFirstPageThumbnail(getFileUrl(right.file_path), { maxWidth: 160 })
        .then(dataUrl => setListThumbs(prev => ({ ...prev, [right.file_path]: dataUrl })))
        .catch(err => console.error('Error generating list PDF thumbnail:', err));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rights]);

  const fetchRights = async () => {
    setLoading(true);
    const res = await adminCrmService.getExclusiveRights(userId);
    if (res?.success) {
      setRights(Array.isArray(res.data) ? res.data : res.data?.rows || []);
    } else {
      setRights([]);
      toast.error(res?.message || 'Failed to load mandate projects');
    }
    setLoading(false);
  };

  const openModal = (right = null) => {
    setMenuOpen(null);
    setEditingRight(right);
    setBuilderName(right?.builder_name || '');
    setStartDate(toInputDate(right?.start_date));
    setEndDate(toInputDate(right?.end_date));
    setSelectedFile(null);
    setExistingFilePath(right?.file_path || null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingRight(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > MAX_PDF_SIZE) {
      toast.error('PDF file size must not exceed 25MB');
      return;
    }
    setSelectedFile(file);
  };

  const handleSave = async () => {
    const missing = [];
    if (!builderName.trim()) missing.push('Builder Name');
    if (!startDate) missing.push('Start Date');
    if (!selectedFile && !existingFilePath) missing.push('PDF Attachment');
    if (missing.length) {
      toast.error(`Required: ${missing.join(', ')}`);
      return;
    }
    if (endDate && endDate < startDate) {
      toast.error('End date cannot be before start date');
      return;
    }

    setSaving(true);
    try {
      let filePath = existingFilePath;
      if (selectedFile) {
        const upload = await propertyService.uploadDocument(selectedFile);
        const url = upload?.data?.url || upload?.url;
        if (!upload?.success || !url) {
          toast.error(upload?.message || 'Failed to upload PDF attachment');
          return;
        }
        filePath = url;
      }

      const payload = {
        builder_name: builderName.trim(),
        start_date: startDate,
        end_date: endDate || null,
        file_path: filePath,
      };

      const res = editingRight
        ? await adminCrmService.updateExclusiveRight(editingRight.id, payload)
        : await adminCrmService.createExclusiveRight(userId, payload);

      if (res?.success) {
        toast.success(editingRight ? 'Mandate project updated successfully' : 'Mandate project added successfully');
        setShowModal(false);
        setEditingRight(null);
        fetchRights();
      } else {
        toast.error(res?.message || 'Failed to save mandate project');
      }
    } catch {
      toast.error('Failed to save mandate project');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    const res = await adminCrmService.deleteExclusiveRight(target.id);
    if (res?.success) {
      toast.success('Mandate project deleted successfully');
      fetchRights();
    } else {
      toast.error(res?.message || 'Failed to delete mandate project');
    }
  };

  const labelStyle = { fontSize: '14px', fontWeight: '500', color: '#262626', marginBottom: '6px', display: 'block' };

  return (
    <div className="d-flex" style={{ background: '#fff', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-3">

          {/* Header: Back + Title + Add */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
              <h6 className="accordion-title mb-0">Mandate Projects</h6>
            </div>
            <button
              onClick={() => openModal()}
              disabled={!userId}
              className="d-flex align-items-center gap-2"
              style={{ background: '#000', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 15px', fontSize: '14px', fontWeight: '500' }}
            >
              <FaPlus size={12} /> Add Agreement
            </button>
          </div>

          {/* User name + role badge */}
          {userName && (
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>{userName}</p>
          )}
          {userRole && (
            <span style={{
              display: 'inline-block', backgroundColor: '#EEF2FF',
              padding: '3px 10px', borderRadius: '8px', fontSize: '12px',
              fontWeight: '600', color: '#4F46E5', marginBottom: '12px'
            }}>
              {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
            </span>
          )}

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div><div className="mt-3" style={{ color: '#888' }}>Loading...</div></div>
          ) : rights.length === 0 ? (
            <div className="card p-4 personal-ids-card text-center" style={{ color: '#9CA3AF' }}>
              No mandate projects added yet.
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {rights.map(right => (
                <div
                  key={right.id}
                  className="d-flex align-items-center justify-content-between gap-3 flex-wrap"
                  style={{ position: 'relative', border: '1px solid #eee', borderRadius: '12px', background: '#F4F4F4', padding: '16px 20px' }}
                >
                  <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: '220px' }}>
                    <div
                      style={{
                        position: 'relative', width: '96px', height: '72px', borderRadius: '8px', border: '1px solid #E0E0E0',
                        background: '#fff', flexShrink: 0
                      }}
                    >
                      {listThumbs[right.file_path] ? (
                        <img src={listThumbs[right.file_path]} alt="First page preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FaFilePdf size={20} color="#9CA3AF" />
                        </div>
                      )}
                      {right.file_path && (
                        <a
                          href={getFileUrl(right.file_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download"
                          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                        >
                          <img src={downloadIcon} alt="Download" width="28" height="28" />
                        </a>
                      )}
                    </div>
                    <div className="d-flex flex-column" style={{ gap: '2px' }}>
                      <span style={{ fontWeight: '600', fontSize: '16px', color: '#000' }}>{right.builder_name}</span>
                      <span style={{ fontSize: '13px', color: '#5F6368' }}>Start Date: {formatDate(right.start_date)}</span>
                      <span style={{ fontSize: '13px', color: '#5F6368' }}>End Date: {formatDate(right.end_date)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setMenuOpen(menuOpen === right.id ? null : right.id)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px' }}
                  >
                    {menuOpen === right.id ? <FaTimes /> : <FaEllipsisV />}
                  </button>

                  {menuOpen === right.id && (
                    <div style={{ position: 'absolute', right: '16px', top: '48px', background: '#000', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10, width: '130px' }}>
                      <div style={{ padding: '10px', cursor: 'pointer', color: '#fff' }} onClick={() => openModal(right)}>Edit</div>
                      <div style={{ padding: '10px', cursor: 'pointer', color: 'red' }} onClick={() => { setMenuOpen(null); setDeleteTarget(right); }}>Delete</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto'
        }}>
          <div className="card p-4" style={{ width: '520px', maxWidth: '95vw', borderRadius: '16px', position: 'relative' }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="accordion-title mb-0">{editingRight ? 'Edit Agreement' : 'Add Agreement'}</h6>
              <button
                onClick={closeModal}
                style={{ width: 32, height: 32, border: '1px solid #9CA3AF', borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
              >
                <FaTimes style={{ fontSize: '14px', color: '#9CA3AF' }} />
              </button>
            </div>

            <div className="form-group mb-3">
              <label style={labelStyle}>Builder Name <span style={{ color: '#E53935' }}>*</span></label>
              <input
                type="text"
                className="form-control personal-input"
                value={builderName}
                onChange={e => setBuilderName(e.target.value)}
                placeholder="Enter builder name"
                maxLength={255}
              />
            </div>

            <div className="d-flex gap-3 mb-3">
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Start Date <span style={{ color: '#E53935' }}>*</span></label>
                <input type="date" className="form-control personal-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>End Date</label>
                <input type="date" className="form-control personal-input" value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>

            <div className="form-group mb-3">
              <label style={labelStyle}>Upload PDF Attachment <span style={{ color: '#E53935' }}>*</span></label>
              {selectedFile || existingFilePath ? (
                <div className="d-flex align-items-center p-2 border rounded gap-2" style={{ height: '56px' }}>
                  <FaFilePdf size={24} color="#DC2626" />
                  <div style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedFile ? selectedFile.name : (
                      <a href={getFileUrl(existingFilePath)} target="_blank" rel="noopener noreferrer">{getFileName(existingFilePath)}</a>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn border-0 bg-transparent p-0"
                    onClick={() => document.getElementById('mandateProjectUpload').click()}
                    style={{ fontSize: '13px', fontWeight: '600', color: '#6F70FA' }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div
                  className="border rounded d-flex flex-column align-items-center justify-content-center w-100"
                  style={{ height: '100px', borderStyle: 'dashed', cursor: 'pointer' }}
                  onClick={() => document.getElementById('mandateProjectUpload').click()}
                >
                  <FaFilePdf size={22} style={{ marginBottom: '6px', color: '#6F70FA' }} />
                  <p className="text-muted m-0" style={{ fontSize: '13px' }}>
                    Upload PDF <span style={{ color: '#6F70FA' }}>Choose File</span>
                  </p>
                </div>
              )}
              <input id="mandateProjectUpload" type="file" className="d-none" accept="application/pdf" onChange={handleFileChange} />
            </div>

            {(modalThumbLoading || modalThumb) && (
              <div className="mb-3">
                <div style={{
                  maxWidth: '220px', minHeight: '160px', margin: '0 auto', border: '1px solid #E2E4E9', borderRadius: '10px',
                  overflow: 'hidden', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {modalThumbLoading ? (
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Loading preview...</span>
                  ) : (
                    <img src={modalThumb} alt="First page preview" style={{ width: '100%', display: 'block' }} />
                  )}
                </div>
                <p className="text-center mb-0 mt-2" style={{ fontSize: '12px', color: '#9CA3AF' }}>First page preview</p>
              </div>
            )}

            <div className="d-flex justify-content-end gap-3 mt-2">
              <button
                onClick={closeModal}
                disabled={saving}
                style={{ border: '1px solid #6F70FA', background: '#fff', color: '#6F70FA', height: '44px', borderRadius: '10px', fontWeight: '500', width: '130px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ border: 'none', background: 'linear-gradient(90deg, #6F70FA, #7367F0)', color: '#fff', height: '44px', borderRadius: '10px', fontWeight: '500', width: '130px', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px'
        }}>
          <div className="card p-4" style={{ maxWidth: '400px', width: '100%', borderRadius: '20px', textAlign: 'center' }}>
            <h6 className="accordion-title mb-2" style={{ fontSize: '17px' }}>Delete Mandate Project?</h6>
            <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6', marginBottom: '24px' }}>
              Are you sure you want to delete the agreement for <strong>{deleteTarget.builder_name}</strong>?
            </p>
            <div className="d-flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="personal-uspbtn flex-grow-1 d-flex justify-content-center"
                style={{ backgroundColor: '#F3F4F6', border: 'none', borderRadius: '10px', padding: '12px' }}
              >
                <span style={{ color: '#374151', fontWeight: '600' }}>Cancel</span>
              </button>
              <button
                onClick={confirmDelete}
                className="personal-uspbtn flex-grow-1 d-flex justify-content-center"
                style={{ backgroundColor: '#DC2626', border: 'none', borderRadius: '10px', padding: '12px' }}
              >
                <span className="personal-uspbtntext">Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
